import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { state } from 'lit/decorators.js';
import { consumeContext } from '../../base/context';
import { setAttribute } from '../../utils/dom';
import { IS_IOS } from '../../utils/support';
import { mediaContext } from '../context';
import { mediaUiElementStyles } from './styles';
export const MEDIA_UI_ELEMENT_TAG_NAME = 'vds-media-ui';
/**
 * This is a general container to hold your UI components but it also enables you to show/hide
 * the player UI when media is not ready for playback by applying styles when the `hidden`
 * attribute is present. It also handles showing/hiding UI depending on whether native UI can't be
 * hidden (*cough* iOS).
 *
 * 💡 The styling is left to you, it will only apply the `hidden` attribute.
 *
 * @tagname vds-media-ui
 * @slot Used to pass in UI components.
 * @example
 * ```html
 * <vds-media-ui>
 *   <!-- ... -->
 * </vds-media-ui>
 * ```
 * @example
 * ```css
 * vds-media-ui {
 *   opacity: 1;
 *   transition: opacity 0.3s ease-out;
 * }
 *
 * vds-media-ui[hidden] {
 *   display: block;
 *   opacity: 0;
 * }
 * ```
 */
export class MediaUiElement extends LitElement {
    constructor() {
        super(...arguments);
        this._mediaCanPlay = mediaContext.canPlay.initialValue;
        this._mediaFullscreen = mediaContext.fullscreen.initialValue;
        this._mediaIsVideoView = mediaContext.isVideoView.initialValue;
        this._mediaPlaysinline = mediaContext.playsinline.initialValue;
    }
    static get styles() {
        return [mediaUiElementStyles];
    }
    static get parts() {
        return [];
    }
    // -------------------------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------------------------
    update(changedProperties) {
        setAttribute(this, 'hidden', this._isUiHidden());
        super.update(changedProperties);
    }
    render() {
        return this._renderDefaultSlot();
    }
    _renderDefaultSlot() {
        return html `<slot></slot>`;
    }
    /**
     * Whether the UI should be hidden.
     */
    _isUiHidden() {
        return (!this._mediaCanPlay ||
            // If iOS Safari and the view type is currently video then we hide the custom UI depending
            // on whether playsinline is set and fullscreen is not active, or if fullscreen is active
            // we should always hide.
            (IS_IOS &&
                this._mediaIsVideoView &&
                (!this._mediaPlaysinline || this._mediaFullscreen)));
    }
}
__decorate([
    state(),
    consumeContext(mediaContext.canPlay)
], MediaUiElement.prototype, "_mediaCanPlay", void 0);
__decorate([
    state(),
    consumeContext(mediaContext.fullscreen)
], MediaUiElement.prototype, "_mediaFullscreen", void 0);
__decorate([
    state(),
    consumeContext(mediaContext.isVideoView)
], MediaUiElement.prototype, "_mediaIsVideoView", void 0);
__decorate([
    state(),
    consumeContext(mediaContext.playsinline)
], MediaUiElement.prototype, "_mediaPlaysinline", void 0);
